/**
 * extracted-protocols — converts the 139-record RI Statewide EMS
 * Protocols JSON catalog (extracted from the v2026.01 PDF) into
 * CareMatrixProtocol records that participate in the runtime alongside
 * the 9 hand-built protocols.
 *
 * The hand-built protocols carry vitals-aware dose gates and
 * weight-based dose calculators (`provenance: 'handbuilt'`). The
 * extracted protocols carry only the prose steps from the published
 * PDF (`provenance: 'engine'`), so their steps render as labels +
 * hints with NO custom dose-gate logic — but they DO participate in
 * cross-protocol satisfaction (a procedure recorded under one
 * protocol shows as satisfied under any other active protocol that
 * lists the same procedure_type).
 *
 * Loading model — Sprint-4 Finding 75 (HIGH):
 *
 *   The previous implementation `import EXTRACTED from './…json'` at
 *   module top inlined the entire 1.9 MB catalog into the main JS
 *   bundle. Every cold load — even on the welcome page or login — had
 *   to download and parse 2 MB before first interaction.
 *
 *   The catalog is now lazy-loaded via Vite's dynamic `import()`,
 *   which splits it into its own hashed chunk file the bundler emits
 *   alongside the main app. `EXTRACTED_PROTOCOLS` exposes the same
 *   array shape as before, but it STARTS EMPTY and fills in once
 *   `loadExtractedProtocols()` resolves. The handbuilt protocols
 *   (the 9 critical-clinical ones) remain synchronous in
 *   care-matrix.ts, so the runtime is never without coverage for
 *   the high-stakes scenarios.
 *
 *   Call `loadExtractedProtocols()` early in app boot (App.tsx does
 *   this idempotently). Multiple callers share one in-flight Promise.
 */

import type { CareMatrixProtocol, CareStep, StepKind } from './care-matrix';
import type { ConsumerId } from './pipeline-graph';
import type { EPCR } from '@/types';

interface ExtractedStep {
  text: string;
  scope?: string | null;
  type?: 'action' | 'decision' | 'note' | string | null;
  medications?: Array<{ drug?: string; dose?: string; route?: string }>;
  references?: string[];
  repeatable?: boolean | null;
}

interface ExtractedProtocol {
  code: string;
  title: string;
  fullTitle?: string;
  slug: string;
  steps?: ExtractedStep[];
  recognition?: string[];
}

interface ExtractedCatalog {
  source?: string;
  effective?: string;
  protocols: ExtractedProtocol[];
}

/** Hand-built ids — matches HANDBUILT_IDS in care-matrix.ts. Listed
 *  again here to break a circular import; kept in sync manually. */
const HANDBUILT_SLUGS = new Set<string>([
  'ri-4-04-acute-coronary-syndrome',
  'ri-4-04-acs',
  'ri-4-04',
  'ri-2-12-stroke',
  'ri-2-04-allergic-reaction',
  'ri-3-03-cardiac-arrest',
  'ri-4-18-overdose',
  'ri-2-08-respiratory',
  'ri-4-04-trauma',
  'ri-2-10-diabetic',
  'ri-2-19-seizure',
]);

/** Default downstream consumers per kind — same defaults the
 *  engine→CareMatrix converter uses. */
const FEEDS_BY_KIND: Record<StepKind, ConsumerId[]> = {
  med:         ['nemsis_xml', 'riemsis_api', 'narrative_engine', 'comstar_billing'],
  procedure:   ['nemsis_xml', 'narrative_engine', 'comstar_billing'],
  assessment:  ['nemsis_xml', 'narrative_engine'],
  vital_set:   ['nemsis_xml', 'narrative_engine'],
  disposition: ['nemsis_xml', 'comstar_billing', 'toc_handoff', 'narrative_engine'],
};

const NEMSIS_BY_KIND: Record<StepKind, string[]> = {
  med: ['eMedications.03', 'eMedications.04', 'eMedications.05', 'eMedications.06'],
  procedure: ['eProcedures.03', 'eProcedures.05', 'eProcedures.06'],
  assessment: ['eExam.01'],
  vital_set: ['eVitals.06', 'eVitals.07', 'eVitals.10', 'eVitals.14'],
  disposition: ['eDisposition.01', 'eDisposition.02'],
};

function inferStepKind(step: ExtractedStep): StepKind {
  if (step.medications && step.medications.length > 0) return 'med';
  const t = (step.text ?? '').toLowerCase();
  if (/transport|destination|hospital|disposition/.test(t)) return 'disposition';
  if (/vitals|reassess|monitor/.test(t)) return 'vital_set';
  if (/establish iv|iv access|intubate|defibrillat|cardiovers|pacing|cpap|oxygen|airway|cpr/.test(t)) return 'procedure';
  return 'assessment';
}

function buildLabel(step: ExtractedStep): string {
  const t = step.text ?? '';
  const first = t.split(/[.!?]\s/)[0] ?? t;
  if (first.length <= 90) return first;
  return first.slice(0, 87) + '…';
}

function buildSteps(p: ExtractedProtocol): CareStep[] {
  const steps = p.steps ?? [];
  return steps.map((s, i): CareStep => {
    const kind = inferStepKind(s);
    const id = `${p.slug}_step_${i + 1}`;
    const label = buildLabel(s);
    const hint = (s.text ?? '').length > label.length ? s.text : undefined;
    const base = {
      id,
      label,
      hint,
      nemsis: NEMSIS_BY_KIND[kind],
      feeds: FEEDS_BY_KIND[kind],
    };
    if (kind === 'med') {
      const m = s.medications?.[0];
      return {
        ...base,
        kind: 'med',
        drug_name: m?.drug ?? 'Unknown',
        dose: m?.dose ?? '',
        unit: '',
        route: m?.route ?? '',
      };
    }
    if (kind === 'procedure') {
      return { ...base, kind: 'procedure', procedure_type: label };
    }
    if (kind === 'disposition') return { ...base, kind: 'disposition' };
    if (kind === 'vital_set')   return { ...base, kind: 'vital_set' };
    return { ...base, kind: 'assessment' };
  });
}

const STOP_WORDS = new Set([
  'with', 'and', 'the', 'for', 'this', 'that', 'from', 'into', 'onto', 'when',
  'patient', 'care', 'protocol', 'standing', 'order', 'orders', 'general',
]);

function buildActivator(p: ExtractedProtocol): (epcr: EPCR) => boolean {
  const tokens = (p.title ?? '')
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter((t) => t.length >= 4 && !STOP_WORDS.has(t));
  if (tokens.length === 0) return () => false;
  return (epcr) => {
    const haystack = [
      epcr.dispatch_reason,
      epcr.chief_complaint,
      epcr.primary_impression,
      ...(epcr.secondary_impressions ?? []),
    ].filter(Boolean).join(' ').toLowerCase();
    if (!haystack) return false;
    return tokens.some((tok) => haystack.includes(tok));
  };
}

function extractedToCareMatrix(p: ExtractedProtocol): CareMatrixProtocol {
  return {
    id: p.slug,
    name: `${p.code} · ${p.title}`,
    version: '2026.01',
    effectiveDate: '2026-04-01',
    citation: `RI Statewide EMS Protocol ${p.code} · v2026.01`,
    provenance: 'engine',
    activates: buildActivator(p),
    steps: buildSteps(p),
  };
}

/**
 * Live array — starts empty, fills in once `loadExtractedProtocols()`
 * resolves. care-matrix.ts spreads this into CARE_MATRIX_PROTOCOLS at
 * module-eval time, BUT the spread captures a reference and our
 * in-place push extends it across the runtime. To keep that contract
 * safe for callers that captured the original reference, we mutate
 * the same array (no `.length = 0; push(...new)` style replacement).
 *
 * The 9 hand-built protocols cover every clinically critical scenario
 * synchronously, so even before this resolves the runtime has
 * full coverage for cardiac arrest / anaphylaxis / etc.
 */
export const EXTRACTED_PROTOCOLS: CareMatrixProtocol[] = [];

let loadPromise: Promise<CareMatrixProtocol[]> | null = null;

/**
 * Idempotently load the extracted-protocols catalog. First call kicks
 * off the dynamic import; subsequent calls return the same Promise.
 * On resolve, `EXTRACTED_PROTOCOLS` is filled in place and the same
 * array is returned.
 *
 * Safe to call from anywhere — fails open: if the chunk can't be
 * loaded (offline first-run, CDN hiccup) the array stays empty and
 * the app continues with handbuilt-only coverage.
 */
export function loadExtractedProtocols(): Promise<CareMatrixProtocol[]> {
  if (loadPromise) return loadPromise;
  loadPromise = (async () => {
    try {
      // Vite splits this dynamic JSON import into its own hashed chunk
      // (verifiable in the production bundle as a separate
      // ri-protocols-extracted.<hash>.js file). On first call the
      // browser fetches that chunk; after that it's served from disk
      // cache + the Workbox precache.
      const mod = await import('./ri-protocols-extracted.json');
      const catalog = (mod.default ?? mod) as unknown as ExtractedCatalog;
      const built = catalog.protocols
        .filter((p) => p.steps && p.steps.length > 0)
        .filter((p) => !HANDBUILT_SLUGS.has(p.slug))
        .map(extractedToCareMatrix);
      // Push in place so any pre-captured reference to
      // EXTRACTED_PROTOCOLS sees the new entries.
      EXTRACTED_PROTOCOLS.length = 0;
      EXTRACTED_PROTOCOLS.push(...built);
      return EXTRACTED_PROTOCOLS;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[extracted-protocols] catalog load failed; runtime uses handbuilt-only coverage', err);
      return EXTRACTED_PROTOCOLS;
    }
  })();
  return loadPromise;
}

/** Current count (0 until the catalog has loaded). */
export function getExtractedProtocolCount(): number {
  return EXTRACTED_PROTOCOLS.length;
}
